const STORAGE_KEY = 'facade_loading_content';
const ROTATION_INTERVAL = 30000; // 30 seconds

/**
 * FIFO cap on locally-cached seen-fact IDs. The server is the source of truth
 * (`/projects/{id}/loading-facts` returns full per-user `seen_ids`), so the
 * local list is only an in-session optimization to avoid re-showing a fact
 * between the moment it's marked and the next API refresh. 1000 × 12-byte
 * hashes ≈ 12 KB — well under the ~5 MB localStorage quota even with other
 * keys present, and large enough that no real session approaches it.
 */
const MAX_SEEN_IDS = 1000;

interface ContentTracker {
  seenContentIds: string[];
  lastSeenTimestamp: number;
  totalRenderCount: number;
  currentSessionStart: number;
}

class LoadingContentTracker {
  private data: ContentTracker;

  constructor() {
    this.data = this.loadData();
  }

  private loadData(): ContentTracker {
    if (typeof window === 'undefined') {
      return this.getDefaultData();
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate data structure
        if (this.isValidData(parsed)) {
          // Trim historical bloat from the pre-cap version of this code.
          if (parsed.seenContentIds.length > MAX_SEEN_IDS) {
            parsed.seenContentIds = parsed.seenContentIds.slice(-MAX_SEEN_IDS);
          }
          return parsed;
        }
      }
    } catch (error) {
      console.error('Failed to load content tracker data:', error);
    }

    return this.getDefaultData();
  }

  private getDefaultData(): ContentTracker {
    return {
      seenContentIds: [],
      lastSeenTimestamp: Date.now(),
      totalRenderCount: 0,
      currentSessionStart: Date.now(),
    };
  }

  private isValidData(data: unknown): data is ContentTracker {
    const tracker = data as Record<string, unknown>;
    return (
      tracker &&
      Array.isArray(tracker.seenContentIds) &&
      typeof tracker.lastSeenTimestamp === 'number' &&
      typeof tracker.totalRenderCount === 'number' &&
      typeof tracker.currentSessionStart === 'number'
    );
  }

  private saveData(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (error) {
      // QuotaExceededError or similar — drop the oldest half of the seen
      // list and retry once. Server still holds the full truth, so local
      // forgetting only risks one duplicate fact before the next API fetch
      // re-seeds `seen_ids`.
      try {
        const half = Math.floor(this.data.seenContentIds.length / 2);
        if (half > 0) {
          this.data.seenContentIds = this.data.seenContentIds.slice(half);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        // Give up silently — tracker stays in-memory only this session.
      }
      console.error('Failed to save content tracker data:', error);
    }
  }

  markContentSeen(contentId: string): void {
    if (!this.data.seenContentIds.includes(contentId)) {
      this.data.seenContentIds.push(contentId);
      // FIFO cap: drop oldest entries past the cap. Order matters — the API
      // re-seeds the full list on every render-screen entry, so the local
      // list is only a brief-lived dedup hint, not authoritative history.
      if (this.data.seenContentIds.length > MAX_SEEN_IDS) {
        this.data.seenContentIds = this.data.seenContentIds.slice(
          -MAX_SEEN_IDS
        );
      }
      this.data.lastSeenTimestamp = Date.now();
      this.saveData();
    }
  }

  getSeenContentIds(): string[] {
    return [...this.data.seenContentIds];
  }

  incrementRenderCount(): void {
    this.data.totalRenderCount++;
    this.saveData();
  }

  getRenderCount(): number {
    return this.data.totalRenderCount;
  }

  shouldRotateContent(): boolean {
    const now = Date.now();
    const timeSinceLastSeen = now - this.data.lastSeenTimestamp;
    return timeSinceLastSeen >= ROTATION_INTERVAL;
  }

  resetSeenContent(): void {
    this.data.seenContentIds = [];
    this.data.lastSeenTimestamp = Date.now();
    this.saveData();
  }

  getSessionDuration(): number {
    return Date.now() - this.data.currentSessionStart;
  }

  startNewSession(): void {
    this.data.currentSessionStart = Date.now();
    this.saveData();
  }
}

// Singleton instance
let trackerInstance: LoadingContentTracker | null = null;

export function getLoadingContentTracker(): LoadingContentTracker {
  if (!trackerInstance) {
    trackerInstance = new LoadingContentTracker();
  }
  return trackerInstance;
}

// Utility functions for easier use
export function getNextContent(currentContentId?: string): string[] {
  const tracker = getLoadingContentTracker();
  const seenIds = tracker.getSeenContentIds();

  if (currentContentId) {
    tracker.markContentSeen(currentContentId);
  }

  return seenIds;
}

export function getUserStats() {
  const tracker = getLoadingContentTracker();
  return {
    renderCount: tracker.getRenderCount(),
    sessionDuration: tracker.getSessionDuration(),
  };
}

export function shouldShowNewContent(): boolean {
  const tracker = getLoadingContentTracker();
  return tracker.shouldRotateContent();
}
